import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { api, getImageUrl } from "../lib/api";
import { useScadaWS } from "../lib/useScadaWS";
import { useAuth } from "../context/AuthContext";
import { MimicWidget } from "./MimicEditor";

export default function MimicViewer() {
  const { id } = useParams();
  const { user } = useAuth();
  const [mimic, setMimic] = useState(null);
  const [tags, setTags] = useState([]);
  const [scale, setScale] = useState(1);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!mimic) return;
    const handleResize = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const w = mimic.bg_width || 1600;
      const h = mimic.bg_height || 900;
      const scaleX = rect.width / w;
      const scaleY = rect.height / h;
      setScale(Math.min(scaleX, scaleY));
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [mimic]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/mimics/${id}`);
        if (!mounted) return;
        setMimic(data);
        const t = await api.get("/tags", { params: { site_id: data.site_id } });
        if (mounted) setTags(t.data);
      } catch { }
    };
    load();
    const iv = setInterval(load, 8000);
    return () => { mounted = false; clearInterval(iv); };
  }, [id]);

  useScadaWS((msg) => {
    if (msg.type === "tag_update") {
      setTags((prev) => prev.map(t => t.id === msg.tag_id ? { ...t, current_value: msg.value, current_status: msg.status } : t));
    }
  });

  if (!mimic) return <div className="min-h-screen bg-[#0B0D0F] text-[#9BA4B5] font-mono-metric flex items-center justify-center tracking-widest text-sm">LOADING MIMIC SYSTEM…</div>;
  const tagById = Object.fromEntries(tags.map(t => [t.id, t]));

  return (
    <div
      ref={containerRef}
      className="w-screen h-screen bg-[#0B0D0F] overflow-hidden flex items-center justify-center"
    >
      <div
        data-testid="mimic-viewer-canvas"
        className="relative origin-center shrink-0"
        style={{
          width: mimic.bg_width ? `${mimic.bg_width}px` : "1600px",
          height: mimic.bg_height ? `${mimic.bg_height}px` : "900px",
          transform: `scale(${scale})`,
          backgroundColor: "#0B0D0F",
          backgroundImage: mimic.background_url
            ? `linear-gradient(rgba(11,13,15,${mimic.bg_opacity ?? 0.55}), rgba(11,13,15,${mimic.bg_opacity ?? 0.55})), url(${getImageUrl(mimic.background_url)})`
            : "none",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {(mimic.widgets || []).map(w => (
          <MimicWidget key={w.id} w={w} tag={tagById[w.tag_id]} />
        ))}
        {(!mimic.widgets || mimic.widgets.length === 0) && (
          <div className="absolute inset-0 flex items-center justify-center text-[#5A6573] font-mono-metric text-sm">
            NO WIDGETS CONFIGURED
          </div>
        )}
      </div>
    </div>
  );
}
